import type { WaitlistEntry } from 'types/waitlist';

import { parseWaitlistStartMs } from 'components/Waitlist/useWaitlistCountdown';

/**
 * Seat-claim state machine — pure, so every branch is testable without React.
 *
 * Problem it solves: while a table is assigning the dealer / small blind / big
 * blind, the engine's atomic seat UPDATE
 * ("... AND p{n}name = '' AND hand NOT IN (1,2,3)" —
 * apps/engine/repositories/sql/texas/updates.js:987) cannot seat a new player.
 * The client used to answer a Buy In tapped in that window with a terminal
 * "Unable to take seat. Please try again." dialog and DROP the request. The
 * window clears in about a second, but the player had to notice the dialog,
 * dismiss it and re-tap — spending a large slice of their 30s waitlist offer on
 * a condition that had nothing to do with them, and sometimes losing the seat.
 *
 * Instead we capture the intent and fire it the moment the table is joinable.
 *
 * INVARIANT: this module can only ever decide to WAIT, to SEND, or to STOP. It
 * never decides that a player IS seated. The engine's atomic UPDATE remains the
 * sole authority on whether a seat is granted, so the worst case for a wrong
 * decision here is one extra request that updates 0 rows.
 */

/**
 * Retry budget for a claim with no seat offer behind it (a plain in-table Join).
 * Offer-driven claims use the server's own offer deadline instead — see
 * offerDeadlineMs. Comfortably longer than an assignment phase, short enough
 * that a wedged table does not leave the player watching a spinner.
 */
export const PLAIN_JOIN_CLAIM_BUDGET_MS = 8000;

/**
 * True when the table's broadcast "hand" sits in the dealer/blinds assignment
 * window, where a join request will be rejected.
 *
 * Deliberately the exact negation of the gate this replaces
 * ("hand === '' || +hand > 3" — BuyInModal, JoinButton, SitOutButton et al),
 * including its treatment of a non-numeric hand as blocked. Note the client is
 * one phase STRICTER than the engine: the engine blocks hand 1-3, the client
 * also holds on hand 0 (DEALER_HAS_BEEN_CHOOSEN). That is preserved on purpose
 * — this change alters WHEN we wait, never WHO may join.
 */
export const isTableInSetup = (hand: unknown): boolean => !(hand === '' || Number(hand) > 3);

/**
 * What the latest broadcast says about the seat we mean to claim. "unknown" is a
 * distinct case on purpose: no snapshot for that seat yet is a reason to keep
 * waiting, NOT a reason to tell the player someone took it.
 */
export type SeatState = 'open' | 'taken' | 'unknown';

/** The player's captured seat-taking intent, held across the setup window. */
export type ClaimIntent = {
  gameId: string | number;
  /**
   * Seat to claim, or 0 for "the engine picks a free seat" — the client's seat
   * lookup reads possibly-unhydrated table state, and services/joinGameParams
   * Rule 2 defers to the engine rather than swallowing the click. A 0 here is
   * therefore a valid claim, not a failure.
   */
  seatId: number;
  amount: number;
  /**
   * The offered table's game variant. Travels WITH the intent because the
   * engine routes and queries the join on it (joinGameParams Rule 1b) and the
   * ambient value goes stale during table switching — a deferred send must not
   * pick up whichever table happens to be current when it finally fires.
   */
  gameType?: unknown;
  /**
   * "<waitForAcceptStartTime>|<bumpCount>" — the same offer-window identity
   * autoBuyInOffer.ts uses. A bump, a re-offer or leaving the queue changes it,
   * which invalidates a claim made against the previous window. null when there
   * is no offer behind this claim (a plain in-table Join).
   */
  offerSig: string | null;
  /**
   * Absolute ms after which we stop retrying. A RETRY BUDGET, never a seating
   * authority — the client timer is not the source of truth for the offer.
   */
  deadlineMs: number;
};

export type ClaimAbortReason = 'seated' | 'seat-taken' | 'offer-revoked' | 'expired';

export type ClaimDecision =
  | { action: 'wait' }
  | { action: 'send' }
  | { action: 'abort'; reason: ClaimAbortReason };

/** Offer-window identity for a waitlist spot; null when there is no spot. */
export const offerSignature = (spot: WaitlistEntry | null | undefined): string | null =>
  spot ? `${spot.waitForAcceptStartTime}|${spot.bumpCount}` : null;

/**
 * Absolute deadline for an offer-driven claim: the server's own offer window
 * (waitForAcceptStartTime + waitlistTimeOut), reusing the countdown's parser so
 * a MySQL-style naive UTC timestamp is read the same way it is on screen.
 * Returns null when the spot carries no usable window.
 */
export const offerDeadlineMs = (spot: WaitlistEntry | null | undefined): number | null => {
  if (!spot || !(spot.waitlistTimeOut > 0)) return null;
  const startMs = parseWaitlistStartMs(spot.waitForAcceptStartTime);
  return Number.isNaN(startMs) ? null : startMs + spot.waitlistTimeOut * 1000;
};

/**
 * Seat occupancy for seatId from a broadcast players array. The array index IS
 * the engine seat number (BuyInModal resolves availableSeatId by findIndex and
 * passes it straight to sitDown), so a missing entry means "no data for that
 * seat", not "empty seat".
 */
export const seatStateFor = (players: unknown, seatId: number): SeatState => {
  if (!Array.isArray(players) || players.length === 0) return 'unknown';
  const seat = players[seatId] as { ID?: unknown } | null | undefined;
  if (!seat) return 'unknown';
  return seat.ID ? 'taken' : 'open';
};

/**
 * Decide what to do with a live claim, given the latest table snapshot.
 *
 * Guard order is load-bearing:
 *   1. "seated" first, so a completed seating is never re-read as anything else.
 *   2. "offer-revoked" BEFORE "seat-taken", so a bumped or expired offer can
 *      never present itself as a retryable seat race.
 *   3. the deadline BEFORE the unknown/setup waits, so a table that never
 *      reports back cannot hold a claim open forever.
 */
export function resolveClaim(input: {
  intent: ClaimIntent;
  hand: unknown;
  seatState: SeatState;
  isSeated: boolean;
  currentOfferSig: string | null;
  nowMs: number;
}): ClaimDecision {
  const { intent, hand, seatState, isSeated, currentOfferSig, nowMs } = input;

  if (isSeated) return { action: 'abort', reason: 'seated' };
  if (intent.offerSig !== null && currentOfferSig !== intent.offerSig) {
    return { action: 'abort', reason: 'offer-revoked' };
  }
  // Seat 0 = "the engine picks" (see ClaimIntent.seatId). There is no specific
  // seat to lose or to wait for a snapshot of, so both seat guards are skipped
  // and the claim turns purely on the table phase.
  const watchesOneSeat = intent.seatId > 0;
  if (watchesOneSeat && seatState === 'taken') return { action: 'abort', reason: 'seat-taken' };
  if (nowMs >= intent.deadlineMs) return { action: 'abort', reason: 'expired' };
  if (watchesOneSeat && seatState === 'unknown') return { action: 'wait' };
  if (isTableInSetup(hand)) return { action: 'wait' };
  return { action: 'send' };
}

/**
 * Player-facing copy, keyed by claim outcome. Kept beside the state machine so
 * a new outcome cannot ship without copy for it.
 */
export const CLAIM_COPY = {
  /** Button label while the intent is held or in flight. */
  pending: 'Claiming Seat',
  /** Body copy replacing the old terminal "Unable to take seat" dialog. */
  retrying: 'Your seat is reserved while the current hand finishes.',
  /** Seat-offer prompt caption once the player has accepted. */
  reserved: 'Your seat is held. Sitting you down now…',
  /**
   * Lost the seat while holding a waitlist spot — their place in the queue
   * stands, so tell them the next seat is still coming to them.
   */
  seatTakenQueued:
    "That seat was just taken. You're still on the waitlist and we'll offer you the next seat that opens.",
  /**
   * Lost the seat as a walk-up joiner with NO waitlist spot — two players can
   * race for the same vacant seat. Nothing is being held for them, so the only
   * honest next step is to pick another seat. Promising a waitlist offer here
   * would be simply untrue.
   */
  seatTakenPlain: 'That seat was just taken. Please choose another empty seat.',
  failed:
    'Something went wrong seating you at this table. You have not been charged. Please try again.'
} as const;

/**
 * Pick the seat-taken message that matches the player's actual situation. The
 * held-claim path is reached by BOTH a waitlisted player acting on a seat offer
 * and an ordinary joiner tapping Buy In, and only the former has a queue place
 * to fall back on.
 */
export const seatTakenMessage = (isQueued: boolean): string =>
  isQueued ? CLAIM_COPY.seatTakenQueued : CLAIM_COPY.seatTakenPlain;
